import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { 
  Home, Plus, Package, Loader2, Download, CheckCircle2,
  Square, CheckSquare, Info
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';

import useHiveTypes from '../hooks/useHiveTypes';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const HIVE_TYPES_FALLBACK = [
  { value: 'langstroth', label: 'Langstroth', prefix: 'LAN' },
  { value: 'horizontal', label: 'Horizontal', prefix: 'HOR' },
  { value: 'layens', label: 'Layens', prefix: 'LAY' },
  { value: 'flow_hive', label: 'Flow Hive', prefix: 'FLO' },
  { value: 'warre', label: 'Warre', prefix: 'WAR' },
  { value: 'top_bar', label: 'Top Bar', prefix: 'TB' },
  { value: 'nuc', label: 'NUC', prefix: 'NUC' },
  { value: 'national', label: 'National', prefix: 'NAT' },
];

const FRAME_SIZES = [
  { value: '8', label: '8 Frame' },
  { value: '10', label: '10 Frame' },
  { value: '5', label: '5 Frame (NUC)' },
];

const COMPONENT_SECTIONS = [
  { id: 'lids_covers', label: 'Lids & Covers' },
  { id: 'bottom_boards_stands', label: 'Bottom Boards & Stands' },
  { id: 'feeders', label: 'Feeders' },
  { id: 'queen_bee_management', label: 'Queen Management' },
  { id: 'pest_control', label: 'Pest Control' },
  { id: 'heavy_accessories', label: 'Accessories' },
];

const BulkAddHives = () => {
  const navigate = useNavigate();
  const { allTypes, adminTypes, getFrameTypes, getFoundationTypes } = useHiveTypes();
  const HIVE_TYPES = allTypes.length > 0 ? allTypes.map(t => ({ value: t.value, label: t.label, prefix: t.label?.substring(0, 3).toUpperCase() || 'H', box_types: t.box_types || [], requires_boxes: t.requires_boxes || (t.box_types?.length > 0) })) : HIVE_TYPES_FALLBACK;
  const [loading, setLoading] = useState(false);
  const [yards, setYards] = useState([]);
  const [createdHives, setCreatedHives] = useState([]);
  const [showSuccess, setShowSuccess] = useState(false);
  const [pdfUrl, setPdfUrl] = useState(null);
  const [boxConfig, setBoxConfig] = useState({});
  const [selectedFrameTypes, setSelectedFrameTypes] = useState([]);
  const [frameQuantities, setFrameQuantities] = useState({});
  const [selectedFoundationTypes, setSelectedFoundationTypes] = useState([]);
  const [foundationQuantities, setFoundationQuantities] = useState({});
  const [adminComponents, setAdminComponents] = useState([]);
  
  const [formData, setFormData] = useState({
    hive_type: 'langstroth',
    quantity: 5,
    frame_count: 10,
    frame_size: '10',
    yard: '',
    components: {}
  });

  useEffect(() => {
    fetchYards();
    fetchAdminComponents();
  }, []);

  // Initialize box config when hive type changes
  const selectedType = HIVE_TYPES.find(t => t.value === formData.hive_type);
  const hasBoxTypes = selectedType?.requires_boxes && selectedType?.box_types?.length > 0;

  useEffect(() => {
    if (hasBoxTypes && selectedType?.box_types) {
      const initial = {};
      selectedType.box_types.forEach((bt, idx) => {
        // Default: first box type gets qty 1, rest get 0
        initial[bt.name] = { qty: idx === 0 ? 1 : 0, size: bt.sizes?.[bt.sizes.length - 1] || '10' };
      });
      setBoxConfig(initial);
    } else {
      setBoxConfig({});
    }
  }, [formData.hive_type, hasBoxTypes]);

  const updateBoxQty = (boxName, qty) => {
    setBoxConfig(prev => ({ ...prev, [boxName]: { ...prev[boxName], qty } }));
  };

  const updateBoxSize = (boxName, size) => {
    setBoxConfig(prev => ({ ...prev, [boxName]: { ...prev[boxName], size } }));
  };

  // Available frame and foundation types for the selected hive type
  const availableFrameTypes = getFrameTypes(formData.hive_type);
  const availableFoundationTypes = getFoundationTypes(formData.hive_type);

  const toggleFrameType = (ft) => {
    if (selectedFrameTypes.includes(ft)) {
      setSelectedFrameTypes(prev => prev.filter(t => t !== ft));
      setFrameQuantities(prev => { const n = { ...prev }; delete n[ft]; return n; });
    } else {
      setSelectedFrameTypes(prev => [...prev, ft]);
      setFrameQuantities(prev => ({ ...prev, [ft]: formData.frame_count || 10 }));
    }
  };

  const toggleFoundationType = (ft) => {
    if (selectedFoundationTypes.includes(ft)) {
      setSelectedFoundationTypes(prev => prev.filter(t => t !== ft));
      setFoundationQuantities(prev => { const n = { ...prev }; delete n[ft]; return n; });
    } else {
      setSelectedFoundationTypes(prev => [...prev, ft]);
      setFoundationQuantities(prev => ({ ...prev, [ft]: formData.frame_count || 10 }));
    }
  };

  const fetchYards = async () => {
    try {
      const token = localStorage.getItem('token');
      if (!token && localStorage.getItem('demoMode') === 'true') return;
      const response = await axios.get(`${API}/yards`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setYards(response.data || []);
    } catch (error) {
    }
  };

  const fetchAdminComponents = async () => {
    try {
      const token = localStorage.getItem('token');
      if (!token) return;
      const response = await axios.get(`${API}/config/admin_components`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const data = response.data;
      setAdminComponents(Array.isArray(data) ? data : (data.items || []));
    } catch (error) {
    }
  };

  const toggleComponent = (key) => {
    setFormData(prev => {
      const current = prev.components[key] || 0;
      return {
        ...prev,
        components: {
          ...prev.components,
          [key]: current > 0 ? 0 : 1
        }
      };
    });
  };

  const updateComponentQty = (key, val) => {
    setFormData(prev => ({
      ...prev,
      components: { ...prev.components, [key]: Math.max(0, parseInt(val) || 0) }
    }));
  };

  const handleSubmit = async () => {
    if (formData.quantity < 1 || formData.quantity > 50) {
      toast.error('Quantity must be between 1 and 50');
      return;
    }

    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.post(`${API}/hives/bulk`, {
        hive_type: formData.hive_type,
        quantity: formData.quantity,
        frame_count: formData.frame_count,
        frame_size: formData.frame_size,
        yard: formData.yard || null,
        components: formData.components,
        prefix: selectedType?.prefix || undefined,
        box_config: hasBoxTypes ? boxConfig : undefined,
        frame_types: selectedFrameTypes.length > 0 ? selectedFrameTypes : undefined,
        frame_quantities: Object.keys(frameQuantities).length > 0 ? frameQuantities : undefined,
        foundation_types: selectedFoundationTypes.length > 0 ? selectedFoundationTypes : undefined,
        foundation_quantities: Object.keys(foundationQuantities).length > 0 ? foundationQuantities : undefined,
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });

      setCreatedHives(response.data.hives || []);
      setPdfUrl(response.data.pdf_url || null);
      setShowSuccess(true);
      toast.success(`Successfully created ${response.data.hives?.length || formData.quantity} hives!`);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create hives');
    } finally {
      setLoading(false);
    }
  };

  const downloadQRCodes = async () => {
    if (!createdHives.length) return;
    
    try {
      const token = localStorage.getItem('token');
      const hiveIds = createdHives.map(h => h.id);
      const response = await axios.post(`${API}/hives/qr-codes-pdf`, 
        { hive_ids: hiveIds },
        { 
          headers: { Authorization: `Bearer ${token}` },
          responseType: 'blob'
        }
      );
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `bulk_hives_qr_codes.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      toast.success('QR codes PDF downloaded!');
    } catch (error) {
      toast.error('Failed to download QR codes');
    }
  };

  // selectedType already declared above

  if (showSuccess) {    return (
      <div className="honeycomb-bg pb-24">
        {/* Header */}
        <div className="bg-white border-b border-stone-200 sticky top-0 z-40 shadow-sm">
          <div className="max-w-7xl p-4 flex items-center justify-between">
            <div className="flex items-center gap-3">
              <Button variant="ghost" size="icon" onClick={() => navigate('/')} className="h-10 w-10">
                <Home className="h-5 w-5" />
              </Button>
              <div>
                <h1 className="text-xl font-bold text-stone-800 flex items-center gap-2">
                  <CheckCircle2 className="h-5 w-5 text-green-600" />
                  Hives Created Successfully!
                </h1>
              </div>
            </div>
          </div>
        </div>

        <div className="max-w-7xl p-4">
          {/* Success Summary */}
          <div className="bg-green-50 border border-green-200 rounded-2xl p-6 mb-6">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-12 h-12 bg-green-100 rounded-full flex items-center justify-center">
                <CheckCircle2 className="h-6 w-6 text-green-600" />
              </div>
              <div>
                <h2 className="text-xl font-bold text-green-800">
                  {createdHives.length} Hives Created
                </h2>
                <p className="text-green-600">Status: Ready for Assignment</p>
              </div>
            </div>

            {/* Created Hives List */}
            <div className="bg-white rounded-xl p-4 border border-green-200 mb-4">
              <h3 className="font-semibold text-stone-700 mb-3">New Hives:</h3>
              <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-2">
                {createdHives.map((hive) => (
                  <div key={hive.id} className="bg-amber-50 border border-amber-200 rounded-lg p-2 text-center">
                    <span className="font-mono font-semibold text-amber-800">
                      {hive.nickname || `Hive ${hive.hive_number}`}
                    </span>
                  </div>
                ))}
              </div>
            </div>

            {/* Actions */}
            <div className="flex flex-col sm:flex-row gap-3">
              <Button
                onClick={downloadQRCodes}
                className="flex-1 bg-amber-600 hover:bg-amber-700 text-white"
              >
                <Download className="h-4 w-4 mr-2" />
                Download QR Codes PDF
              </Button>
              <Button
                onClick={() => navigate('/?view=hives')}
                variant="outline"
                className="flex-1"
              >
                View All Hives
              </Button>
              <Button
                onClick={() => { setShowSuccess(false); setCreatedHives([]); }}
                variant="outline"
                className="flex-1"
              >
                <Plus className="h-4 w-4 mr-2" />
                Add More
              </Button>
            </div>
          </div>

          {/* Info */}
          <div className="bg-blue-50 border border-blue-200 rounded-xl p-4 flex items-start gap-3">
            <Info className="h-5 w-5 text-blue-600 flex-shrink-0 mt-0.5" />
            <div className="text-sm text-blue-700">
              <p className="font-medium">Next Steps:</p>
              <ul className="list-disc list-inside mt-1 space-y-1">
                <li>These hives are now available in Swarms and Split dropdowns</li>
                <li>When assigning a swarm or split, update the Yard, Bee Variety, and Origin</li>
                <li>Print QR codes and attach to your physical hives</li>
              </ul>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="honeycomb-bg pb-24">
      {/* Header */}
      <div className="p-4">
        <div className="max-w-7xl">
          <h1 className="text-2xl font-bold text-stone-800 flex items-center gap-2">
            <Package className="h-6 w-6 text-amber-600" />
            Bulk Add Hives
          </h1>
          <p className="text-sm text-stone-500 mt-1">Prepare multiple hives for swarm/split season</p>
        </div>
      </div>

      <div className="max-w-7xl p-4">
        {/* Info Banner */}
        <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 mb-6 flex items-start gap-3">
          <Info className="h-5 w-5 text-amber-600 flex-shrink-0 mt-0.5" />
          <div className="text-sm text-amber-800">
            <p className="font-medium">Bulk Add for Season Prep</p>
            <p className="mt-1">
              Create multiple prepared hives at once. They'll be marked as "Ready" and available 
              for assignment when you capture a swarm or make a split.
            </p>
          </div>
        </div>

        {/* Form */}
        <div className="bg-white rounded-2xl border border-stone-200 shadow-sm p-6 space-y-6">
          {/* Hive Type & Quantity */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <Label className="text-stone-700 font-medium">Hive Type</Label>
              <Select 
                value={formData.hive_type} 
                onValueChange={(v) => setFormData({ ...formData, hive_type: v })}
              >
                <SelectTrigger className="h-12 mt-1">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {HIVE_TYPES.map((type) => (
                    <SelectItem key={type.value} value={type.value}>
                      {type.label} ({type.prefix}-XXX)
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            
            <div>
              <Label className="text-stone-700 font-medium">Quantity to Create</Label>
              <Input
                type="number"
                min="1"
                max="50"
                value={formData.quantity}
                onChange={(e) => setFormData({ ...formData, quantity: parseInt(e.target.value) || 1 })}
                className="h-12 mt-1 text-lg font-semibold"
              />
              <p className="text-xs text-stone-500 mt-1">Max 50 hives per batch</p>
            </div>
          </div>

          {/* Box Type Configuration — for hive types with configurable boxes */}
          {hasBoxTypes && selectedType?.box_types ? (
            <div>
              <Label className="text-stone-700 font-medium mb-3 block">Box Configuration</Label>
              <div className="space-y-3">
                {selectedType.box_types.map((bt) => {
                  const cfg = boxConfig[bt.name] || { qty: 0, size: bt.sizes?.[bt.sizes.length - 1] || '10' };
                  return (
                    <div key={bt.name} className="flex items-center gap-3 p-3 rounded-xl border border-stone-200 bg-stone-50" data-testid={`box-type-${bt.name.toLowerCase().replace(/\s+/g, '-')}`}>
                      <div className="flex-1 min-w-0">
                        <p className="font-medium text-stone-800 text-sm">{bt.name}</p>
                      </div>
                      <div className="flex items-center gap-2">
                        <Label className="text-xs text-stone-500 whitespace-nowrap">Qty</Label>
                        <div className="flex items-center border border-stone-300 rounded-lg overflow-hidden">
                          <button
                            type="button"
                            onClick={() => updateBoxQty(bt.name, Math.max(0, cfg.qty - 1))}
                            className="px-2.5 py-1.5 bg-stone-100 hover:bg-stone-200 text-stone-700 font-bold text-sm"
                          >-</button>
                          <span className={`px-3 py-1.5 text-sm font-semibold min-w-[2rem] text-center ${cfg.qty > 0 ? 'bg-amber-100 text-amber-800' : 'bg-white text-stone-400'}`}>
                            {cfg.qty}
                          </span>
                          <button
                            type="button"
                            onClick={() => updateBoxQty(bt.name, Math.min(bt.max_qty || 6, cfg.qty + 1))}
                            className="px-2.5 py-1.5 bg-stone-100 hover:bg-stone-200 text-stone-700 font-bold text-sm"
                          >+</button>
                        </div>
                      </div>
                      {bt.sizes?.length > 1 && (
                        <div className="flex items-center gap-2">
                          <Label className="text-xs text-stone-500 whitespace-nowrap">Size</Label>
                          <Select value={cfg.size} onValueChange={(v) => updateBoxSize(bt.name, v)}>
                            <SelectTrigger className="h-9 w-20">
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              {bt.sizes.map(s => (
                                <SelectItem key={s} value={s}>{s}F</SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
              <p className="text-xs text-stone-500 mt-2">Set the number and size of each box type for your hives</p>
            </div>
          ) : (
            /* Frame Configuration — for simple hive types without boxes */
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <Label className="text-stone-700 font-medium">Frames per Hive</Label>
                <Input
                  type="number"
                  min="1"
                  max="30"
                  value={formData.frame_count}
                  onChange={(e) => setFormData({ ...formData, frame_count: parseInt(e.target.value) || 10 })}
                  className="h-12 mt-1"
                />
              </div>
              
              <div>
                <Label className="text-stone-700 font-medium">Frame Size</Label>
                <Select 
                  value={formData.frame_size} 
                  onValueChange={(v) => setFormData({ ...formData, frame_size: v })}
                >
                  <SelectTrigger className="h-12 mt-1">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {FRAME_SIZES.map((size) => (
                      <SelectItem key={size.value} value={size.value}>
                        {size.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
          )}

          {/* Default Yard (Optional) */}
          <div>
            <Label className="text-stone-700 font-medium">Default Yard (Optional)</Label>
            <Select 
              value={formData.yard || "none"} 
              onValueChange={(v) => setFormData({ ...formData, yard: v === "none" ? "" : v })}
            >
              <SelectTrigger className="h-12 mt-1">
                <SelectValue placeholder="Leave blank - assign when used" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="none">No yard (assign later)</SelectItem>
                {yards.map((yard) => (
                  <SelectItem key={yard.id} value={yard.name}>
                    {yard.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <p className="text-xs text-stone-500 mt-1">
              You can assign a yard now, or leave blank and assign when allocating to a swarm/split
            </p>
          </div>

          {/* Components — matching Edit Hive layout */}
          <div>
            <Label className="text-stone-700 font-medium mb-3 block">Hive Components</Label>
            {adminComponents.length > 0 ? (
              <div className="space-y-4">
                {COMPONENT_SECTIONS.map(section => {
                  const sectionComponents = adminComponents.filter(c => c.section === section.id);
                  if (sectionComponents.length === 0) return null;
                  return (
                    <div key={section.id}>
                      <p className="text-xs font-semibold text-stone-500 uppercase tracking-wide mb-2">{section.label}</p>
                      <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
                        {sectionComponents.map(comp => {
                          const compKey = comp.name.toLowerCase().replace(/\s+/g, '_');
                          const qty = formData.components[compKey] || 0;
                          return (
                            <button
                              key={comp.id || comp.name}
                              type="button"
                              onClick={() => toggleComponent(compKey)}
                              className={`flex items-center gap-2 p-2.5 rounded-xl border-2 transition-all text-left ${
                                qty > 0
                                  ? 'border-amber-500 bg-amber-50 text-amber-800'
                                  : 'border-stone-200 bg-white text-stone-600 hover:border-stone-300'
                              }`}
                            >
                              {qty > 0 ? <CheckSquare className="h-4 w-4 text-amber-600 flex-shrink-0" /> : <Square className="h-4 w-4 flex-shrink-0" />}
                              <span className="text-sm font-medium truncate">{comp.name}</span>
                            </button>
                          );
                        })}
                      </div>
                    </div>
                  );
                })}
              </div>
            ) : (
              <p className="text-sm text-stone-400">Loading components...</p>
            )}
          </div>

          {/* Frame Types */}
          {availableFrameTypes.length > 0 && (
            <div>
              <Label className="text-stone-700 font-medium mb-3 block">Frame Types</Label>
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
                {availableFrameTypes.map((ft) => (
                  <button
                    key={ft}
                    type="button"
                    onClick={() => toggleFrameType(ft)}
                    className={`flex items-center gap-2 p-2.5 rounded-xl border-2 transition-all text-sm ${
                      selectedFrameTypes.includes(ft)
                        ? 'border-sky-500 bg-sky-50 text-sky-800'
                        : 'border-stone-200 bg-white text-stone-600 hover:border-stone-300'
                    }`}
                  >
                    {selectedFrameTypes.includes(ft) ? <CheckSquare className="h-4 w-4 text-sky-600" /> : <Square className="h-4 w-4" />}
                    <span className="font-medium">{ft}</span>
                  </button>
                ))}
              </div>
              {selectedFrameTypes.length > 0 && (
                <div className="mt-2 flex flex-wrap gap-2">
                  {selectedFrameTypes.map(ft => (
                    <div key={ft} className="flex items-center gap-2 bg-sky-50 border border-sky-200 rounded-lg px-3 py-1.5">
                      <span className="text-xs font-medium text-sky-800">{ft}</span>
                      <Input
                        type="number" min="1" max="100"
                        value={frameQuantities[ft] || ''}
                        onChange={(e) => setFrameQuantities(prev => ({ ...prev, [ft]: parseInt(e.target.value) || 0 }))}
                        className="w-16 h-7 text-xs text-center"
                      />
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* Foundation Types */}
          {availableFoundationTypes.length > 0 && (
            <div>
              <Label className="text-stone-700 font-medium mb-3 block">Foundation Types</Label>
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
                {availableFoundationTypes.map((ft) => (
                  <button
                    key={ft}
                    type="button"
                    onClick={() => toggleFoundationType(ft)}
                    className={`flex items-center gap-2 p-2.5 rounded-xl border-2 transition-all text-sm ${
                      selectedFoundationTypes.includes(ft)
                        ? 'border-emerald-500 bg-emerald-50 text-emerald-800'
                        : 'border-stone-200 bg-white text-stone-600 hover:border-stone-300'
                    }`}
                  >
                    {selectedFoundationTypes.includes(ft) ? <CheckSquare className="h-4 w-4 text-emerald-600" /> : <Square className="h-4 w-4" />}
                    <span className="font-medium">{ft}</span>
                  </button>
                ))}
              </div>
              {selectedFoundationTypes.length > 0 && (
                <div className="mt-2 flex flex-wrap gap-2">
                  {selectedFoundationTypes.map(ft => (
                    <div key={ft} className="flex items-center gap-2 bg-emerald-50 border border-emerald-200 rounded-lg px-3 py-1.5">
                      <span className="text-xs font-medium text-emerald-800">{ft}</span>
                      <Input
                        type="number" min="1" max="100"
                        value={foundationQuantities[ft] || ''}
                        onChange={(e) => setFoundationQuantities(prev => ({ ...prev, [ft]: parseInt(e.target.value) || 0 }))}
                        className="w-16 h-7 text-xs text-center"
                      />
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* Preview */}
          <div className="bg-stone-50 rounded-xl p-4 border border-stone-200">
            <h3 className="font-semibold text-stone-700 mb-2">Preview</h3>
            <div className="text-sm text-stone-600 space-y-1">
              <p>
                <strong>{formData.quantity}</strong> x {selectedType?.label || 'Hives'}
                {hasBoxTypes ? '' : ` (${formData.frame_count} frames, ${formData.frame_size}F)`}
              </p>
              {hasBoxTypes && (
                <p>
                  Boxes: {Object.entries(boxConfig)
                    .filter(([, v]) => v.qty > 0)
                    .map(([name, v]) => `${v.qty}x ${name} (${v.size}F)`)
                    .join(', ') || 'None selected'}
                </p>
              )}
              <p>
                Naming: <span className="font-mono bg-amber-100 px-2 py-0.5 rounded">
                  {selectedType?.prefix}-001
                </span> to <span className="font-mono bg-amber-100 px-2 py-0.5 rounded">
                  {selectedType?.prefix}-{String(formData.quantity).padStart(3, '0')}
                </span>
              </p>
              <p>
                Components: {Object.entries(formData.components)
                  .filter(([_, v]) => v > 0)
                  .map(([k]) => k.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase()))
                  .join(', ') || 'None'}
              </p>
              {selectedFrameTypes.length > 0 && (
                <p>Frames: {selectedFrameTypes.map(ft => `${ft} (${frameQuantities[ft] || 0})`).join(', ')}</p>
              )}
              {selectedFoundationTypes.length > 0 && (
                <p>Foundation: {selectedFoundationTypes.map(ft => `${ft} (${foundationQuantities[ft] || 0})`).join(', ')}</p>
              )}
              <p>Status: <span className="text-amber-600 font-medium">Ready</span></p>
            </div>
          </div>

          {/* Submit */}
          <Button
            onClick={handleSubmit}
            disabled={loading}
            className="w-full h-14 text-lg font-semibold bg-amber-600 hover:bg-amber-700 text-white rounded-xl"
          >
            {loading ? (
              <>
                <Loader2 className="h-5 w-5 mr-2 animate-spin" />
                Creating Hives...
              </>
            ) : (
              <>
                <Plus className="h-5 w-5 mr-2" />
                Create {formData.quantity} Hives
              </>
            )}
          </Button>
        </div>
      </div>
    </div>
  );
};

export default BulkAddHives;
